import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Link } from "react-router-dom";

const isInternalPath = (href: string) =>
  href.startsWith("/") && !href.startsWith("//");

const components: Components = {
  a({ href, children }) {
    const url = href ?? "";
    if (isInternalPath(url)) {
      return (
        <Link
          to={url}
          className="text-[#42D49C] underline underline-offset-2 hover:text-[#6ee7b7]"
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={url}
        target={url.startsWith("mailto:") ? undefined : "_blank"}
        rel={url.startsWith("mailto:") ? undefined : "noopener noreferrer"}
        className="text-[#42D49C] underline underline-offset-2 hover:text-[#6ee7b7]"
      >
        {children}
      </a>
    );
  },
  img({ src, alt }) {
    // Only render images hosted on this site.
    const url = typeof src === "string" ? src : "";
    if (!isInternalPath(url)) return null;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block my-2">
        <img
          src={url}
          alt={alt ?? ""}
          loading="lazy"
          className="max-h-60 w-auto max-w-full rounded-lg border border-white/15 object-cover"
        />
      </a>
    );
  },
  strong({ children }) {
    return <strong className="font-semibold text-white">{children}</strong>;
  },
  em({ children }) {
    return <em className="italic text-gray-100">{children}</em>;
  },
  u({ children }) {
    return <u className="underline underline-offset-2">{children}</u>;
  },
  ul({ children }) {
    return (
      <ul className="my-1.5 list-disc space-y-1 pl-5 text-gray-200">{children}</ul>
    );
  },
  ol({ children }) {
    return (
      <ol className="my-1.5 list-decimal space-y-1 pl-5 text-gray-200">
        {children}
      </ol>
    );
  },
  li({ children }) {
    return <li className="leading-relaxed">{children}</li>;
  },
  p({ children }) {
    return <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>;
  },
  code({ children }) {
    return (
      <code className="rounded bg-black/40 px-1 py-0.5 text-[0.9em] text-amber-100">
        {children}
      </code>
    );
  },
};

type ChatMarkdownProps = {
  text: string;
};

export default function ChatMarkdown({ text }: ChatMarkdownProps) {
  return (
    <div className="chat-md mt-1 text-gray-200">
      <ReactMarkdown
        components={components}
        rehypePlugins={[rehypeRaw]}
        unwrapDisallowed
        allowedElements={[
          "p",
          "strong",
          "em",
          "u",
          "a",
          "ul",
          "ol",
          "li",
          "code",
          "br",
          "img",
        ]}
        urlTransform={(url) => url}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
